import React, { useMemo } from 'react';

import { Autocomplete, Checkbox, TextField } from '@sunwisesoftware/sunwise-ui';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';

const ReactHookFormSelect2Multi = ({
    allOptionKey = '__select--all__',
    allText = i18next.t('All text'),
    blurOnSelect = true,
    clearOnBlur,
    control,
    disabled = false,
    freeSolo,
    fullWidth = false,
    isClearable = true,
    isLoading = false,
    label,
    limitTags,
    multiple,
    name,
    onBlur,
    onChange,
    onInputChange,
    options,
    renderOption,
    required,
    size = 'small',
    sortOptions = true,
    sx,
    variant,
}) => {
    const sortedOptions = useMemo(() => {
        if (!options) return [];
        const optionsWithGroup = [];

        for (const group of options) {
            for (const option of group.options)
                optionsWithGroup.push({ ...option, group: group.label });
        }

        if (sortOptions)
            optionsWithGroup.sort((a, b) => -b.group?.localeCompare(a.group));

        if (multiple)
            optionsWithGroup.unshift({ label: allText, value: allOptionKey });

        return optionsWithGroup;
    }, [multiple, options, sortOptions]);

    const getDataValue = (data) => {
        if (!multiple) return data?.value ?? '';

        if (data?.length === sortedOptions?.length) return [];

        if (data?.find((option) => option.value === allOptionKey))
            return sortedOptions
                .filter((option) => option.value !== allOptionKey)
                .map((option) => option.value);

        return (
            data?.map((option) =>
                typeof option === 'object' ? option.value : option,
            ) || []
        );
    };

    const getOptionDisabled = (option) =>
        option?.disabled || option.value === '';

    const getOptionLabel = (option) => option?.label || '';

    const getValue = (field) => {
        if (freeSolo)
            return multiple
                ? field?.value?.map((val) => ({ label: val, value: val })) || []
                : field?.value || null;

        if (multiple)
            return (
                sortedOptions?.filter((option) =>
                    field.value?.includes(option.value),
                ) ?? []
            );

        return (
            sortedOptions?.find((option) => option.value === field.value) ??
            null
        );
    };

    const isOptionEqualToValue = (option, value) =>
        option.value === value?.value;

    const onChangeAction = (_, data, field, onChange) => {
        const value = getDataValue(data);
        const { ignoreFieldChange = false } = data || {};
        if (!ignoreFieldChange) field.onChange(value);
        if (onChange) onChange(value, field.onChange);
    };

    const renderOptions = (props, option, state, ownerState) => {
        const { selected } = state;
        const { multiple, options, value } = ownerState;
        return (
            <li {...props} key={`${option.value}-${option.label}`}>
                {renderOption ? (
                    renderOption(option)
                ) : (
                    <>
                        {multiple && (
                            <Checkbox
                                checked={
                                    selected ||
                                    value.length === options.length - 1
                                }
                            />
                        )}
                        {option.label}
                    </>
                )}
            </li>
        );
    };

    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState: { error } }) => (
                <Autocomplete
                    blurOnSelect={blurOnSelect && !multiple}
                    clearOnBlur={clearOnBlur}
                    disableClearable={!isClearable}
                    disableCloseOnSelect={multiple}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    freeSolo={freeSolo}
                    getOptionDisabled={getOptionDisabled}
                    getOptionLabel={getOptionLabel}
                    groupBy={(option) => option.group}
                    isOptionEqualToValue={isOptionEqualToValue}
                    limitTags={limitTags}
                    loading={isLoading}
                    multiple={multiple}
                    name={field.name}
                    onChange={(event, data) =>
                        onChangeAction(event, data, field, onChange)
                    }
                    onBlur={(e) =>
                        onBlur?.({ newValue: e.target.value, ...field })
                    }
                    onInputChange={(_, newValue, reason) =>
                        onInputChange?.({ newValue, reason, ...field })
                    }
                    options={sortedOptions}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={Boolean(error)}
                            helperText={error?.message}
                            inputRef={field.ref}
                            label={label}
                            variant={variant}
                        />
                    )}
                    renderOption={renderOptions}
                    required={required}
                    size={size}
                    sx={sx}
                    value={getValue(field)}
                />
            )}
        />
    );
};

ReactHookFormSelect2Multi.propTypes = {
    allOptionKey: PropTypes.string,
    allText: PropTypes.string,
    blurOnSelect: PropTypes.bool,
    clearOnBlur: PropTypes.bool,
    control: PropTypes.object,
    disabled: PropTypes.bool,
    freeSolo: PropTypes.bool,
    fullWidth: PropTypes.bool,
    id: PropTypes.string,
    ignore: PropTypes.array,
    isClearable: PropTypes.bool,
    isLoading: PropTypes.bool,
    label: PropTypes.string,
    limitTags: PropTypes.number,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onInputChange: PropTypes.func,
    options: PropTypes.array,
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    renderOption: PropTypes.func,
    required: PropTypes.bool,
    size: PropTypes.string,
    sortOptions: PropTypes.bool,
    sx: PropTypes.object,
    variant: PropTypes.string,
};

export default ReactHookFormSelect2Multi;
